import { Link } from 'react-router-dom';
import {
  Phone,
  MessageCircle,
  MapPin,
  ArrowRight,
  Shield,
  Clock,
  Award,
  Users,
  CheckCircle,
} from 'lucide-react';
import { SEO } from '../components/common';
import { Button, ServiceCard } from '../components/ui';
import GoogleReviewsSection from '../components/ui/GoogleReviewsSection';
import {
  CONTACT_INFO,
  SERVICES,
  STATS,
} from '../utils/constants';

/**
 * Homepage component
 */
const HomePage = () => {
  const featuredServices = SERVICES.filter((s) => s.featured);

  const breadcrumbs = [
    { name: 'Home', url: '/' },
  ];

  return (
    <>
      <SEO
        title="Best MRI & Diagnostic Center in Kasur"
        description="Mian Tufail Diagnostic Center offers advanced MRI services in Kasur with Siemens Magnetom C2 Open MRI technology. Whole body MRI, MR angiogram, brain MRI & more. Book appointment: +92 300 047 8311"
        keywords="MRI Kasur, MRI scan Kasur, best MRI center Kasur, diagnostic center near me, Siemens Magnetom C2, Open MRI Pakistan, whole body MRI Kasur, MR angiogram, MR venogram, brain MRI, spine MRI, medical imaging Kasur, radiology Kasur, Mian Tufail Diagnostic Center"
        url="/"
        breadcrumbs={breadcrumbs}
        services={featuredServices}
      />

      {/* Hero Section */}
      <section className="relative bg-hero-pattern overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl animate-float" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-xl animate-float animate-delay-200" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-xl animate-float animate-delay-400" />
        </div>

        <div className="container-custom relative py-20 md:py-28 lg:py-36">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="text-white space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-sm font-medium">Now Open for Appointments</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-display-lg font-heading font-bold leading-tight">
                Accurate Diagnosis,{' '}
                <span className="text-primary-200">Better Treatment</span>
              </h1>

              <p className="text-lg md:text-xl text-primary-100 max-w-lg leading-relaxed">
                State-of-the-art medical imaging facility providing advanced MRI 
                and diagnostic services using the latest Siemens technology.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to="/book-appointment">
                  <Button variant="accent" size="lg" icon={ArrowRight} iconPosition="right">
                    Book Appointment
                  </Button>
                </Link>
                <a href={`tel:${CONTACT_INFO.phone}`}>
                  <Button variant="secondary" size="lg" icon={Phone}>
                    {CONTACT_INFO.phoneDisplay}
                  </Button>
                </a>
              </div>

              {/* Quick Contact */}
              <div className="flex flex-wrap items-center gap-6 pt-4">
                <a
                  href={CONTACT_INFO.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
                >
                  <MessageCircle className="w-5 h-5 text-green-400" />
                  <span className="text-sm">WhatsApp Us</span>
                </a>
                <div className="flex items-center gap-2 text-white/90">
                  <MapPin className="w-5 h-5 text-primary-300" />
                  <span className="text-sm">{CONTACT_INFO.address.short}</span>
                </div>
              </div>
            </div>

            {/* Hero Image/Card */}
            <div className="relative hidden lg:block">
              <div className="relative bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500/30 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent-500/20 rounded-full blur-2xl" />
                
                <div className="relative space-y-6">
                  {/* MRI Machine Image */}
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img 
                      src="/images/services/siemens-mri.jpg" 
                      alt="Siemens Magnetom C2 Open MRI Machine" 
                      className="w-full h-48 object-cover"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-md">
                      <span className="text-primary-600 font-bold text-lg">S</span>
                    </div>
                    <div>
                      <p className="font-semibold text-white text-lg">Siemens Magnetom C2</p>
                      <p className="text-primary-200 text-sm">Latest Open MRI Technology</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {STATS.map((stat, index) => (
                      <div key={index} className="bg-white/10 rounded-xl p-4">
                        <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                        <p className="text-primary-200 text-sm">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Safe & Secure', desc: 'Non-invasive imaging' },
              { icon: Clock, title: 'Quick Results', desc: '6-8 hour reports; emergency delivery in 2 hours' },
              { icon: Award, title: 'Expert Radiologists', desc: 'Certified professionals' },
              { icon: Users, title: 'Patient Comfort', desc: 'Open MRI design' },
            ].map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4 rounded-xl hover:bg-primary-50 transition-colors"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-3">
                  <item.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-neutral-900">{item.title}</h3>
                <p className="text-sm text-neutral-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-medical-gradient">
        <div className="container-custom">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">
              Our Services
            </span>
            <h2 className="section-title mb-4">
              Services We <span className="gradient-text">Provide</span>
            </h2>
            <p className="section-subtitle mx-auto">
              Providing a comprehensive range of diagnostic services with precision and care.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredServices.map((service) => (
              <ServiceCard
                key={service.id}
                title={service.title}
                description={service.shortDescription}
                icon={service.icon}
                featured={service.featured}
                onClick={() => {}}
              />
            ))}
          </div>

          {/* View All Services CTA */}
          <div className="text-center">
            <Link to="/services">
              <Button variant="outline" icon={ArrowRight} iconPosition="right">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <span className="inline-block text-primary-600 font-semibold text-sm uppercase tracking-wider mb-3">
                Why Choose Us
              </span>
              <h2 className="section-title mb-6">
                Advanced Technology,{' '}
                <span className="gradient-text">Expert Care</span>
              </h2>
              <p className="text-neutral-600 mb-8 leading-relaxed">
                At Mian Tufail Diagnostic Center, we combine cutting-edge Siemens MRI 
                technology with experienced radiologists to deliver accurate diagnoses 
                and exceptional patient care.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  'Latest Siemens Magnetom C2 Open MRI System',
                  'Experienced and certified radiologists',
                  'Comfortable open design for all patients',
                  'Reports delivered in 6-8 hours, with emergency report delivery in 2 hours',
                  'Affordable rates with insurance support',
                  'Convenient location with easy parking',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-primary-600 flex-shrink-0" />
                    <span className="text-neutral-700">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/contact">
                <Button variant="primary" icon={ArrowRight} iconPosition="right">
                  Learn More About Us
                </Button>
              </Link>
            </div>

            {/* Image/Stats Card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Our Commitment to Excellence</h3>
                
                <div className="grid grid-cols-2 gap-6">
                  {STATS.map((stat, index) => (
                    <div key={index} className="text-center p-4 bg-white/10 rounded-xl">
                      <p className="text-3xl md:text-4xl font-bold">{stat.value}</p>
                      <p className="text-primary-200 text-sm mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-white/10 rounded-xl">
                  <p className="text-center text-primary-100 italic">
                    &ldquo;Your health is our priority. We ensure every patient receives 
                    the highest quality diagnostic care.&rdquo;
                  </p>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -z-10 -top-4 -right-4 w-full h-full bg-primary-100 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviewsSection />

      {/* CTA Section */}
      <section className="py-20 bg-primary-700">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Book Your Appointment?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Schedule your MRI scan today and take the first step towards accurate diagnosis 
            and better treatment.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment">
              <Button variant="accent" size="lg" icon={ArrowRight} iconPosition="right">
                Book Appointment
              </Button>
            </Link>
            <a
              href={CONTACT_INFO.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary" size="lg" icon={MessageCircle}>
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
